import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  AlertTriangle,
  Clock,
  Globe,
  MapPin,
  ShieldCheck,
  Syringe,
} from "lucide-react";
import { StoreButtons } from "@/components/landing/StoreButtons";
import { PageHero } from "@/components/layout/PageHero";
import { pages } from "@/content/pages";
import { diseaseName } from "@/content/travel/diseases";
import type { Locale } from "@/i18n/locales";
import { localePath } from "@/i18n/locales";
import {
  countriesWithAdvice,
  countryName,
  diseasesWithAdvice,
  sortRegionsByPopularity,
} from "@/lib/travel-data";

type TravelViewProps = {
  locale: Locale;
};

const tipIcons = [Clock, ShieldCheck, AlertTriangle, Globe];
const regionImages: Record<string, string> = {
  "zuidoost-azie": "/images/travel/southeast-asia.webp",
  "sub-sahara-afrika": "/images/travel/africa.webp",
  "zuid-amerika": "/images/travel/south-america.webp",
  "midden-oosten": "/images/travel/middle-east.webp",
  "europa-noord-amerika": "/images/travel/europe.webp",
  "zuid-centraal-azie": "/images/travel/south-asia.webp",
  cariben: "/images/travel/south-america.webp",
  "oost-azie": "/images/travel/east-asia.webp",
  oceanie: "/images/travel/destination.webp",
};

export function TravelView({ locale }: TravelViewProps) {
  const copy = pages[locale].travel;

  return (
    <main className="bg-background">
      <PageHero eyebrow={copy.eyebrow} title={copy.title} intro={copy.intro} />

      <section className="pb-16">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-5 px-5 sm:grid-cols-2 lg:grid-cols-4">
          {copy.tips.map((tip, index) => {
            const Icon = tipIcons[index] ?? Clock;
            return (
              <motion.article
                key={tip.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h2 className="font-heading font-semibold text-[#1a3a5c]">
                  {tip.title}
                </h2>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {tip.desc}
                </p>
              </motion.article>
            );
          })}
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-5xl px-5">
          <h2 className="mb-8 font-heading text-2xl font-bold text-[#1a3a5c] md:text-3xl">
            {copy.regionsTitle}
          </h2>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sortRegionsByPopularity(copy.regions).map((region, index) => (
              <motion.article
                key={region.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.07 }}
                className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm transition-shadow hover:shadow-md"
              >
                <Link
                  to={localePath(locale, `/travel/${region.slug}`)}
                  className="block"
                >
                  <div className="h-44 overflow-hidden">
                    <img
                      src={
                        regionImages[region.slug] ??
                        "/images/travel/destination.webp"
                      }
                      alt=""
                      width={600}
                      height={400}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <div className="mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <h3 className="font-heading font-bold text-[#1a3a5c]">
                        {region.name}
                      </h3>
                    </div>
                    <ul className="mb-3 space-y-1.5">
                      {region.vaccines.map((vaccine) => (
                        <li
                          key={vaccine}
                          className="flex items-center gap-2 text-sm text-foreground"
                        >
                          <Syringe className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
                          {vaccine}
                        </li>
                      ))}
                    </ul>
                    <p className="mt-3 border-t border-border pt-3 text-xs leading-relaxed text-muted-foreground">
                      {region.note}
                    </p>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
          <h2 className="mb-6 mt-16 font-heading text-2xl font-bold text-[#1a3a5c] md:text-3xl">
            {copy.allDestinationsTitle}
          </h2>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {[...countriesWithAdvice()]
              .sort((a, b) =>
                countryName(a, locale).localeCompare(
                  countryName(b, locale),
                  locale,
                ),
              )
              .map((country) => (
                <li key={country.slug}>
                  <Link
                    to={localePath(locale, `/travel/${country.slug}`)}
                    className="block rounded-lg px-2 py-1.5 text-sm font-medium text-[#1a3a5c] hover:bg-primary/5 hover:text-primary"
                  >
                    {countryName(country, locale)}
                  </Link>
                </li>
              ))}
          </ul>
          <h2 className="mb-6 mt-16 font-heading text-2xl font-bold text-[#1a3a5c] md:text-3xl">
            <Link
              to={localePath(locale, "/travel/diseases")}
              className="hover:text-primary"
            >
              {copy.diseasesTitle}
            </Link>
          </h2>
          <p className="mb-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {copy.diseasesIntro}
          </p>
          <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {diseasesWithAdvice().map((disease) => (
              <li key={disease.slug}>
                <Link
                  to={localePath(locale, `/travel/diseases/${disease.slug}`)}
                  className="block rounded-lg px-2 py-1.5 text-sm font-medium text-[#1a3a5c] hover:bg-primary/5 hover:text-primary"
                >
                  {diseaseName(disease.slug, locale)}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="pb-24">
        <div className="mx-auto max-w-3xl px-5">
          <div className="rounded-3xl bg-[#1a3a5c] p-10 text-center text-white">
            <ShieldCheck className="mx-auto mb-4 h-10 w-10 text-primary" />
            <h2 className="mb-3 font-heading text-2xl font-bold md:text-3xl">
              {copy.ctaTitle}
            </h2>
            <p className="mb-6 text-base leading-relaxed text-white/70">
              {copy.ctaText}
            </p>
            <StoreButtons locale={locale} className="justify-center" />
          </div>
        </div>
      </section>
    </main>
  );
}
